import { useState, useEffect, useRef } from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'

import { selectUserId } from '~/auth/slices/auth.slice'
import { useGetBootcampListUserDataQuery } from '~/bootcamp/apis/get-list.api'

import CircleIconButton from '~/bootcamp/components/CircleIconButton'
import { CATEGORY_COLORS, CATEGORY_PATHS } from '~/bootcamp/constants/bootcamp.const'
import useProcessGymsListData from '~/bootcamp/hooks/useProcessGymsListData'
import { CategoryPathUrls } from '~/bootcamp/types/bootcamp.types'
import { useAppSelector } from '~/redux/hooks'
import ROUTES from '~/shared/constants/routes.const'
import { BootCampItem, BootCampItemGroq, bootcampOverviewGroq } from '~/types/bootcamp'
import { Breakpoint, useCheckBreakpoint } from '~/utils'
import VideoTopicIcon from '~/bootcamp/components/VideoTopicIcon'

import VideoCourseList from '~/bootcamp/pages/BootcampList/VideoCourseList'
import useProcessVideoCourseListData from '~/bootcamp/hooks/useProcessVideoCourseListData'
import { Link } from '~/components/Link'
import { checkTagValidity } from '~/bootcamp/utils/bootcamp.utils'
import BootcampStatusTag from '~/bootcamp/components/BootcampStatusTag'
import SVGIcon from '~/components/Icons/svgs/SVGIcon'
import ChevronRightIcon from '~/components/Icons/svgs/ChevronRightIcon'
import TitleWithEnLabel from '~/bootcamp/components/TitleWithEnLabel'
import bootcampAnalytics from '~/analytics/bootcamp/bootcampAnalytics'
import { BootcampEvents } from '~/analytics/bootcamp/bootcampEvents.types'

const HEADER_TRANS_KEYS = {
  [CATEGORY_PATHS.algebra]: 'algebra',
  [CATEGORY_PATHS.geometry]: 'geometry',
  [CATEGORY_PATHS.numbersAndQuantities]: 'numbersAndQuantities',
  [CATEGORY_PATHS.statisticsAndProbability]: 'statisticsAndProbability',
  [CATEGORY_PATHS.functions]: 'functions',
  [CATEGORY_PATHS.proofs]: 'proofs',
} as const

const defaultBootcampsOverview: bootcampOverviewGroq = {
  name: '',
  metaDescription: '',
}

function BootcampVideoTopic({ subjectList }: { subjectList: BootCampItemGroq[] }): React.ReactElement {
  const router = useRouter()
  const { query, locale } = router
  const { key } = query
  const [bootcampKey, setBootcampKey] = useState(subjectList[subjectList.findIndex((sub) => sub.key === key) | 0]?.key)
  const isMobile = useCheckBreakpoint({ lt: Breakpoint.MD })
  const [collapsedItems, setCollapsedItems] = useState<boolean[]>(() => (isMobile ? subjectList.map(() => true) : []))
  const [activeIndex, setActiveIndex] = useState<number>(subjectList.findIndex((sub) => sub.key === key) | 0)

  const { t } = useTranslation('bootcamp')
  const userId = useAppSelector(selectUserId)
  const bootcampSubtopic = query.subtopic as CategoryPathUrls
  const backToLink = ROUTES.BOOTCAMP
  const headingTransKey = HEADER_TRANS_KEYS[bootcampSubtopic]
  const { bgClass, borderClass } = CATEGORY_COLORS[bootcampSubtopic]
  const pageHeading = t(headingTransKey)
  const selectRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    router.replace(`${ROUTES.BOOTCAMP}/${bootcampSubtopic}/${key}`, undefined, {
      shallow: true,
    })
    setActiveIndex(subjectList.findIndex((sub) => sub.key === key))
    setBootcampKey(key as string)
    if (!isMobile) {
      setCollapsedItems(() => {
        return subjectList.map((sub) => sub.key === key)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjectList, key])

  useEffect(() => {
    setCollapsedItems(() => {
      if (isMobile) {
        return subjectList.map(() => true)
      }
      return subjectList.map((sub) => sub.key === key)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile])

  const { data: userVideoTopicData } = useGetBootcampListUserDataQuery(
    {
      gymCategory: bootcampSubtopic,
      userId: userId || undefined,
    },
    { skip: !userId },
  )
  const listData = useProcessGymsListData(subjectList, userVideoTopicData)

  const courseList = useProcessVideoCourseListData(bootcampSubtopic, userId, subjectList)

  useEffect(() => {
    if (isMobile && selectRef?.current) {
      const index = listData.findIndex((item) => item.key === key)
      if (index !== -1) {
        const listItem = selectRef.current.children[index]
        const timer = setTimeout(() => {
          listItem.scrollIntoView({
            block: isMobile ? 'start' : 'center',
            inline: 'nearest',
            behavior: 'smooth',
          })
        }, 400)

        return () => clearTimeout(timer)
      }
    }
  }, [isMobile, key, listData])

  const onVideoTopicClick = (index: number): void => {
    setBootcampKey(listData[index].key)
    setActiveIndex(index)
    setCollapsedItems((prev) => {
      let collapsed = [...prev]
      if (isMobile) {
        collapsed[index] = !collapsed[index]
      } else {
        collapsed = prev.map(() => false)
        collapsed[index] = true
      }
      return collapsed
    })
  }

  // const trackHelpClick = (isOpen: boolean): void => {
  //   bootcampAnalytics.logClickHelpButton({ category: query.subtopic as string, status: isOpen ? 'open' : 'close' })
  // }

  const trackUpcomingSubjectClick = (subjectKey: string): void => {
    bootcampAnalytics.log(BootcampEvents.CLICK_UPCOMING_SUBJECT, {
      locale,
      category: bootcampSubtopic,
      subject: subjectKey,
    })
  }
  const getSubjectElement = (data: BootCampItem, index: number): React.ReactElement => {
    const routePath = `${ROUTES.BOOTCAMP}/${bootcampSubtopic}/${data.key}`
    const isTagValid = data.tagStatus?.tag && checkTagValidity(data.tagStatus?.validity)

    const showVideoCourseListMobile = Boolean(isMobile && !!subjectList[index]?.courses && collapsedItems[index])

    return (
      <div
        className={`w-full pt-4 md:pt-0 ${isTagValid ? 'mt-4' : ''}`}
        key={data.key}
        onClick={data?.isUpcoming ? () => trackUpcomingSubjectClick(data.key) : undefined}
      >
        <Link className="w-full" scroll={false} shallow to={routePath}>
          {isTagValid && <BootcampStatusTag className="absolute -mt-4 ml-2" status={data.tagStatus?.tag} />}
          <button
            className={`cursor-pointer w-full min-h-7 px-2 text-left flex items-center justify-between  rounded ${borderClass} ${
              !collapsedItems[index] ? 'border' : isMobile ? 'text-md font-semibold border' : 'bg-purple-light-deep'
            }`}
            key={data.key}
            onClick={() => onVideoTopicClick(index)}
          >
            <TitleWithEnLabel isUpcoming={data?.isUpcoming} name={data.name} />
            <span className={`transform duration-200 ease-out ${!collapsedItems[index] ? 'rotate-0' : 'rotate-90'}`}>
              <SVGIcon icon={ChevronRightIcon} pathClassName="text-black" size={16} />
            </span>
          </button>
        </Link>
        {showVideoCourseListMobile && (
          <VideoCourseList
            bootcampKey={data.key}
            className="mobile"
            courses={courseList[index]}
            data={subjectList[index]?.courses || defaultBootcampsOverview}
            subtopic={bootcampSubtopic}
          />
        )}
      </div>
    )
  }
  return (
    <section>
      <CircleIconButton
        aria-label={t('backToBootcamp')}
        className="w-9 h-9 bg-white mb-4 md:mb-0"
        iconColor="text-purple"
        iconName="arrow-left-bold"
        link={backToLink}
      />

      <section className="md:mt-8 md:w-176 md:mx-auto">
        <section className="grid gap-4 p-2 sm:p-4 md:p-0 bg-white rounded min-h-132">
          <div className="w-full grid md:grid-cols-2" role="menu">
            <article className="flex flex-col gap-3 font-normal md:pl-6 md:p-4" role="menuitem">
              <div className={`h-9 flex gap-2 items-center px-2 py-1 text-white rounded tracking-normal ${bgClass}`}>
                <VideoTopicIcon topicKey={bootcampSubtopic} />
                <span>{pageHeading}</span>
              </div>
              <div className="grid gap-0 sm:gap-4" ref={selectRef}>
                {listData.map((bootcamp, index) => getSubjectElement(bootcamp, index))}
              </div>
            </article>
            {subjectList[activeIndex]?.courses && !isMobile && (
              <article
                className="p-4 rounded"
                onClick={
                  subjectList[activeIndex]?.isUpcoming ? () => trackUpcomingSubjectClick(bootcampKey) : undefined
                }
              >
                <VideoCourseList
                  bootcampKey={bootcampKey}
                  courses={courseList[activeIndex]}
                  data={subjectList[activeIndex]?.courses || defaultBootcampsOverview}
                  isBootcampSubjectUpcoming={subjectList[activeIndex]?.isUpcoming}
                  subtopic={bootcampSubtopic}
                />
              </article>
            )}
          </div>
        </section>
      </section>
    </section>
  )
}

export default BootcampVideoTopic
